<template>
    <v-container class="overflow-auto pa-5 ma-0">
        <v-form ref="form" style="max-width: 900px" @input="$emit('input', $event)">
            <v-row dense>
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <h4>{{ $t('name_contact') }}</h4>
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" sm="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('status')">
                        <v-checkbox
                            class="mt-0 pt-0"
                            :input-value="record?.active"
                            :label="$t('aktiv')"
                            hide-details="auto"
                            @change="onInput('active', $event)"
                        />
                    </LabelField>
                </v-col>
                <v-col cols="12" sm="6">
                    <v-switch
                        v-if="isAKModuleConfigured"
                        :input-value="record?.ak_module_active"
                        :label="`${$t('ak_module_active')}: ${record?.ak_module_active ? $t('active') : $t('inaktiv')}`"
                        :color="record?.ak_module_active ? 'success' : ''"
                        @change="onInput('ak_module_active', $event)"
                    />
                </v-col>
            </v-row>
            <v-row
                v-bind="{
                    dense: !$vuetify.breakpoint.mdAndDown,
                    noGutters: $vuetify.breakpoint.mdAndDown,
                }"
            >
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('name')">
                        <v-text-field
                            hide-details="auto"
                            :value="record?.lastname"
                            counter="100"
                            :rules="getRules('required')"
                            dense
                            outlined
                            @input="onInput('lastname', $event)"
                        />
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('vorname')">
                        <v-text-field
                            hide-details="auto"
                            :value="record?.firstname"
                            counter="100"
                            :rules="getRules('required')"
                            dense
                            outlined
                            @input="onInput('firstname', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>
            <v-row
                v-bind="{
                    dense: !$vuetify.breakpoint.mdAndDown,
                    noGutters: $vuetify.breakpoint.mdAndDown,
                }"
            >
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('role_name')">
                        <v-select
                            :value="record?.role"
                            :items="roles.models"
                            item-text="label"
                            item-value="key"
                            hide-details="auto"
                            dense
                            outlined
                            :disabled="!userIsAdmin()"
                            :rules="getRules('required')"
                            @input="
                                onInput('role', $event);
                                validateForm();
                            "
                        />
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('email')">
                        <v-text-field
                            hide-details="auto"
                            :value="record?.email"
                            counter="255"
                            :rules="getRules('required')"
                            dense
                            outlined
                            @input="onInput('email', $event)"
                        >
                            <template #append>
                                <a :href="`mailto:${record.email}`">
                                    <KpIcon icon-cls="link" color="primary" disabled-color="grey" />
                                </a>
                            </template>
                        </v-text-field>
                    </LabelField>
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('assign_organizer')">
                        <EventOrganizerSelect
                            :event="dummyEventForEo"
                            :required="isEoRequired()"
                            @selected="onEventOrganizerSelect($event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>
            <v-row class="mt-6" dense>
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <h4>{{ $t('login') }}</h4>
                </v-col>
            </v-row>
            <v-row
                v-bind="{
                    dense: !$vuetify.breakpoint.mdAndDown,
                    noGutters: $vuetify.breakpoint.mdAndDown,
                }"
            >
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('username')">
                        <v-text-field
                            hide-details="auto"
                            :value="record?.username"
                            counter="100"
                            :hint="!record?.id ? $t('username_eindeutig_hint') : null"
                            :rules="getRules('required')"
                            dense
                            outlined
                            @input="onInput('username', $event)"
                        />
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('id')">
                        <v-text-field :value="record?.id ?? $t('neu')" disabled dense outlined hide-details="auto" />
                    </LabelField>
                </v-col>
            </v-row>
            <v-row
                v-bind="{
                    dense: !$vuetify.breakpoint.mdAndDown,
                    noGutters: $vuetify.breakpoint.mdAndDown,
                }"
            >
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('new_pw')">
                        <v-text-field
                            hide-details="auto"
                            :value="record?.newPw"
                            type="password"
                            :rules="getPWFieldRules()"
                            dense
                            outlined
                            @input="
                                onInput('newPw', $event);
                                validateForm();
                            "
                        />
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('confirm_pw')">
                        <v-text-field
                            hide-details="auto"
                            :value="record?.newPwConfirm"
                            :rules="getPWConfirmFieldRules()"
                            type="password"
                            dense
                            outlined
                            @input="onInput('newPwConfirm', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" sm="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('password_change')">
                        <v-checkbox
                            class="mt-0 pt-0"
                            :input-value="record?.change_flag"
                            hide-details="auto"
                            :label="$t('set_new_password')"
                            @change="onInput('change_flag', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>
            <v-row
                v-bind="{
                    dense: !$vuetify.breakpoint.mdAndDown,
                    noGutters: $vuetify.breakpoint.mdAndDown,
                }"
            >
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <v-row class="mt-6" dense>
                        <v-col cols="12">
                            <h4>{{ $t('infomail') }}</h4>
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="12">
                            <v-checkbox
                                class="mt-0 pt-0"
                                :input-value="record?.infomail_tgk"
                                :label="$t('infomails_erhalten')"
                                hide-details="auto"
                                @change="onInput('infomail_tgk', $event)"
                            />
                            <div v-if="record?.infomail_tgk_date.isValid()" class="info-text">
                                {{ $t('zusagedatum_infomail') }}:
                                {{ record?.infomail_tgk_date.formatIf('dd, DD.MM.YYYY') }}
                            </div>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <v-row class="mt-6" dense>
                        <v-col cols="12">
                            <h4>{{ $t('kulturverbund') }}</h4>
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="12">
                            <LabelField :label="$t('assign_kulturverbund')">
                                <OrganizerGroupSelectField
                                    :value="record?.organizer_group_id"
                                    :display-value="record?.organizer_group"
                                    :collection="organizerGroupCollection"
                                    :disabled="!userIsAdmin()"
                                    @selected="onOrganizerGroupSelect($event)"
                                />
                            </LabelField>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
            <v-row
                v-bind="{
                    dense: !$vuetify.breakpoint.mdAndDown,
                    noGutters: $vuetify.breakpoint.mdAndDown,
                }"
            >
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('sprache')">
                        <LangSelect :value="record?.locale || 'de'" @input="onInput('locale', $event)" />
                    </LabelField>
                </v-col>
            </v-row>
        </v-form>
    </v-container>
</template>

<script>
import { ROLES, User } from '@/models/User';
import { getRoles } from '@/models/Role';
import getRulesMixin from '@/mixins/get-form-rules';
import { formRules } from '@/helpers/tools';
import { EventOrganizerCollection } from '@/models/EventOrganizer';
import { OrganizerGroupCollection } from '@/models/OrganizerGroup';
import OrganizerGroupSelectField from '@/components/organizer_group/SelectField.vue';
import { userIsAdmin } from '@/helpers/auth';
import LangSelect from '@/components/LangSelect.vue';
import { Event } from '@/models/Event';
import EventOrganizerSelect from '@/components/event/EventOrganizerSelect.vue';
import { globalParam } from '@/helpers/globals';

export default {
    name: 'UserBasisdaten',
    components: {
        OrganizerGroupSelectField,
        LangSelect,
        EventOrganizerSelect,
    },
    mixins: [getRulesMixin],

    props: {
        record: {
            type: User,
            required: true,
        },
    },

    data() {
        return {
            roles: getRoles(),
            eventOrganizerCollection: new EventOrganizerCollection(),
            organizerGroupCollection: new OrganizerGroupCollection(),
        };
    },

    computed: {
        // We need a dummy event to use the EventOrganizerSelect field: This field
        // uses an existing event to extract its display values and EO ids.
        dummyEventForEo() {
            const event = new Event();
            event.organizer_id = this.record?.event_organizer_id;

            event.event_organizer =
                this.record?.event_organizer?.displayTitle ||
                this.record?.event_organizer?.title ||
                `${this.record?.event_organizer?.firstname || ''} ${this.record?.event_organizer?.surname || ''}`;
            return event;
        },
        isAKModuleConfigured() {
            return globalParam('user.ak_module_active');
        },
    },

    mounted() {
        this.validateForm();
    },

    methods: {
        userIsAdmin,

        validateForm() {
            this.$refs.form.validate();
        },

        getPWFieldRules() {
            return [formRules.minLengthOptional(8)];
        },

        getPWConfirmFieldRules() {
            return [() => this.record?.newPw === this.record?.newPwConfirm || this.$t('pw_not_match')];
        },

        isEoRequired() {
            return this.record?.role === ROLES.EVENT_ORGANIZER;
        },

        onInput(field, value) {
            this.record.set(field, value);
        },

        onEventOrganizerSelect(rec) {
            // Nach der Record selektiert wurde setzen wir sowohl den Display-Wert (damit man gleich sieht
            // was gewählt wurde), sowie die ID des Records auf dem User:
            if (rec) {
                this.record?.set('event_organizer_id', rec.id);
                this.record?.set('event_organizer', rec);
            } else {
                this.record?.set('event_organizer_id', null);
                this.record?.set('event_organizer', null);
            }
        },

        onOrganizerGroupSelect(rec) {
            // Nach der Record selektiert wurde setzen wir sowohl den Display-Wert (damit man gleich sieht
            // was gewählt wurde), sowie die ID des Records auf dem User:
            if (rec) {
                this.record?.set('organizer_group_id', rec.id);
                this.record?.set('organizer_group', rec.title);
            } else {
                this.record?.set('organizer_group_id', null);
                this.record?.set('organizer_group', null);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.info-text {
    font-size: 14px;
    text-indent: 32px;
    opacity: 0.36;
}
</style>
